import { motion } from 'framer-motion';
import { useState, useEffect, useRef } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Header from "../landings/components/Header";
import HeroSection from "../landings/components/HeroSection";
import FeaturesSection from "../landings/components/FeaturesSection";
import HowItWorksSection from "../landings/components/HowItWorksSection";
import PricingSection from "../landings/components/PricingSection";
import TestimonialsSection from "../landings/components/TestimonialsSection";
import CtaSection from "../landings/components/CtaSection";
import Footer from "../landings/components/Footer";
import FeaturesPage from "../landings/pages/FeaturesPage";
import PricingPage from "../landings/pages/PricingPage";
import ResourcesPage from "../landings/pages/ResourcesPage";
import CategoryPage from "../landings/pages/resources/CategoryPage";
import ErrorBoundary from "../landings/components/ErrorBoundary";

// Homepage component
function HomePage() {
  // Mouse position for background effects
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const mouseMoveRef = useRef<number>(0);
  
  // Smooth scroll to section when URL has hash
  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.substring(1);
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, []);

  // Handle mouse movement for background effects
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (mouseMoveRef.current) {
        cancelAnimationFrame(mouseMoveRef.current);
      }
      
      mouseMoveRef.current = requestAnimationFrame(() => {
        setMousePosition({ x: e.clientX, y: e.clientY });
      });
    };
    
    window.addEventListener("mousemove", handleMouseMove);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      if (mouseMoveRef.current) {
        cancelAnimationFrame(mouseMoveRef.current);
      }
    };
  }, []);

  return (
    <div className="min-h-screen relative">
      {/* Full-page background that extends under everything */}
      <div className="fixed inset-0 w-full h-full overflow-hidden z-0">
        {/* Base gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-white via-[#fff5eb] to-[#f9f6f4]"></div>
        
        {/* Animated grid pattern */}
        <svg className="absolute inset-0 w-full h-full opacity-[0.07]" width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M0 10 L40 10" stroke="#946e5d" strokeWidth="0.5" fill="none" />
              <path d="M0 20 L40 20" stroke="#946e5d" strokeWidth="0.5" fill="none" />
              <path d="M0 30 L40 30" stroke="#946e5d" strokeWidth="0.5" fill="none" />
              <path d="M10 0 L10 40" stroke="#946e5d" strokeWidth="0.5" fill="none" />
              <path d="M20 0 L20 40" stroke="#946e5d" strokeWidth="0.5" fill="none" />
              <path d="M30 0 L30 40" stroke="#946e5d" strokeWidth="0.5" fill="none" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>

        {/* Decorative shapes */}
        <motion.div 
          className="absolute -right-32 top-20 w-[600px] h-[600px] rounded-full opacity-20 bg-gradient-to-tl from-[#ff8c42]/40 to-[#ff8c42]/0"
          animate={{ 
            scale: [1, 1.05, 1],
            rotate: [0, 5, 0]
          }}
          transition={{ 
            duration: 20, 
            repeat: Infinity, 
            repeatType: "reverse",
            ease: "easeInOut" 
          }}
        />

        <motion.div 
          className="absolute -left-32 bottom-0 w-[500px] h-[500px] rounded-full opacity-20 bg-gradient-to-br from-[#946e5d]/40 to-[#946e5d]/0"
          animate={{ 
            scale: [1, 1.1, 1],
            rotate: [0, -5, 0]
          }}
          transition={{ 
            duration: 15, 
            repeat: Infinity, 
            repeatType: "reverse",
            ease: "easeInOut",
            delay: 2
          }}
        />
        
        {/* Mouse-following gradient spotlight */}
        <motion.div
          className="absolute w-[600px] h-[600px] rounded-full bg-gradient-to-r from-[#ff8c42]/15 to-[#946e5d]/10 blur-3xl pointer-events-none"
          style={{ 
            left: mousePosition.x - 300,
            top: mousePosition.y - 300,
            opacity: 0.7
          }}
          transition={{ type: "tween", ease: "backOut", duration: 0.5 }}
        />
      </div>
      
      {/* Main Content */}
      <div className="relative z-10">
        <Header />
        <main>
          <HeroSection />
          <FeaturesSection />
          <HowItWorksSection /> 
          <TestimonialsSection />
          <PricingSection />
          <CtaSection />
        </main>
        <Footer />
      </div>
    </div>
  );
}


function LandingRoutes() {
  return (
    <ErrorBoundary>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/resources" element={<ResourcesPage />} />
        <Route path="/resources/:category" element={<CategoryPage />} />
        
        {/* Fallback for any unmatched landing routes */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ErrorBoundary>
  );
}

export default LandingRoutes;